import { useCallback, useMemo } from 'react';
import { AuthorizationService, HttpService } from '@bentley/pw-api';
import { useBuddi } from '@bentley/pw-config';
import { assertHttpServiceConfigured, handleBadResponse, handleDuplicateConnectionName, handleExistingPrimaryConnection } from '../../errors/errors';
import { t } from '../../translate';
import { toApiConnection, toConnection } from './conversion';
/**
 * ConnectionApi for engaging with ProjectWise Web Connections.
 * @param workAreaConnectionsConfig Configuration for the WorkArea Connections.
 * @param workAreaConnectionsConfig.buddiRegionCode Region code for BUDDI
 * @param workAreaConnectionsConfig.contextId Context ID for the connections
 * @param workAreaConnectionsConfig.getOidcToken Async function to get OIDC token
 * @returns ConnectionApi
 */
export function useWorkAreaConnections({ buddiRegionCode, contextId, getOidcToken }) {
    const pwWebConnectionsUrl = useBuddi('ProjectWiseWebConnections.URL', buddiRegionCode);
    const httpService = useMemo(() => {
        if (!pwWebConnectionsUrl) {
            return undefined;
        }
        return new HttpService({
            authorization: new AuthorizationService({ getOidcToken }),
            baseUrl: `${pwWebConnectionsUrl}api/v1/context/${contextId}`
        });
    }, 
    /* eslint-disable-next-line react-hooks/exhaustive-deps --
     * getOidcToken should not re-trigger
     */
    [contextId, pwWebConnectionsUrl]);
    const getAll = useCallback(async () => {
        assertHttpServiceConfigured(httpService);
        const apiConnections = [];
        let continuationToken = '';
        do {
            let headers = {};
            if (continuationToken) {
                headers = { ContinuationToken: continuationToken };
            }
            try {
                const response = await httpService.get('connections', { headers });
                handleBadResponse(response);
                const data = (await response.json());
                const validConnections = data.filter((connection) => connection.id && connection.name && connection.url);
                apiConnections.push(...validConnections);
                continuationToken = response.headers.get('ContinuationToken');
            }
            catch (e) {
                console.error(e);
                throw new Error(t('connections.failedToLoad'));
            }
        } while (continuationToken);
        const connections = apiConnections.map(toConnection);
        return connections;
    }, [httpService]);
    const addOne = useCallback(async (connection, isImport) => {
        assertHttpServiceConfigured(httpService);
        const connections = await getAll();
        handleDuplicateConnectionName(connections, connection);
        const url = isImport ? 'connections/import' : 'connections';
        const body = JSON.stringify(toApiConnection(connection));
        const response = await httpService.post(url, body);
        handleBadResponse(response);
        const apiConnection = (await response.json());
        return toConnection(apiConnection);
    }, [getAll, httpService]);
    const updateOne = useCallback(async (connection) => {
        assertHttpServiceConfigured(httpService);
        const url = `connections/${connection.Id}`;
        const { Url, ...connectionWithoutUrl } = connection;
        const body = JSON.stringify(toApiConnection({ ...connectionWithoutUrl }));
        const response = await httpService.put(url, body);
        handleBadResponse(response);
        const apiConnection = (await response.json());
        return toConnection(apiConnection);
    }, [httpService]);
    const upgradeOne = useCallback(async (connectionId, upgradeVersion) => {
        assertHttpServiceConfigured(httpService);
        const url = `connections/${connectionId}/upgrade`;
        const body = JSON.stringify({ upgradeVersion });
        const response = await httpService.post(url, body);
        handleBadResponse(response);
        const apiConnection = (await response.json());
        return toConnection(apiConnection);
    }, [httpService]);
    const deleteMany = useCallback(async (connectionIds) => {
        assertHttpServiceConfigured(httpService);
        for (const connectionId of connectionIds) {
            const url = `connections/${connectionId}`;
            const response = await httpService.delete(url, null);
            if (response.status == 400) {
                throw new Error(t('connections.cannotDeletePrimary'));
            }
            handleBadResponse(response);
        }
    }, [httpService]);
    const addSynced = useCallback(async (connectionId) => {
        assertHttpServiceConfigured(httpService);
        const url = `user-sync/${connectionId}`;
        const response = await httpService.post(url, null);
        handleBadResponse(response);
    }, [httpService]);
    const getSynced = useCallback(async () => {
        assertHttpServiceConfigured(httpService);
        const url = 'user-sync';
        const response = await httpService.get(url);
        if (response.status == 404) {
            return [];
        }
        handleBadResponse(response);
        const data = (await response.json());
        return data.connections;
    }, [httpService]);
    const deleteSynced = useCallback(async (connectionIds) => {
        assertHttpServiceConfigured(httpService);
        for (const connectionId of connectionIds) {
            const url = `user-sync/${connectionId}`;
            const response = await httpService.delete(url, null);
            handleBadResponse(response);
        }
    }, [httpService]);
    const addDriveSynced = useCallback(async (connectionId, backfill) => {
        assertHttpServiceConfigured(httpService);
        const withBackfill = backfill ? 'backfill/' : '';
        const url = `drive-sync/${withBackfill}${connectionId}`;
        const response = await httpService.post(url, null);
        handleBadResponse(response);
    }, [httpService]);
    const getDriveSynced = useCallback(async () => {
        assertHttpServiceConfigured(httpService);
        const url = 'drive-sync';
        const response = await httpService.get(url);
        if (response.status == 404) {
            return null;
        }
        handleBadResponse(response);
        const driveSync = (await response.json());
        return driveSync;
    }, [httpService]);
    const setMultiMode = useCallback(async (multiMode) => {
        assertHttpServiceConfigured(httpService);
        const url = `drive-sync/multi-mode/${String(multiMode)}`;
        const response = await httpService.put(url, null);
        handleBadResponse(response);
    }, [httpService]);
    const deleteDriveSynced = useCallback(async (connectionId) => {
        assertHttpServiceConfigured(httpService);
        const url = `drive-sync/${connectionId}`;
        const response = await httpService.delete(url, null);
        handleBadResponse(response);
    }, [httpService]);
    const unsetPrimaryConnection = useCallback(async () => {
        assertHttpServiceConfigured(httpService);
        const url = 'primary-connection';
        const response = await httpService.delete(url, null);
        handleBadResponse(response);
    }, [httpService]);
    const setPrimaryConnection = useCallback(async (connectionId, primary) => {
        assertHttpServiceConfigured(httpService);
        if (primary === false) {
            return unsetPrimaryConnection();
        }
        const url = `primary-connection/${connectionId}`;
        const response = await httpService.post(url, null);
        await handleExistingPrimaryConnection(response);
        handleBadResponse(response);
    }, [httpService, unsetPrimaryConnection]);
    const getPrimaryConnection = useCallback(async () => {
        assertHttpServiceConfigured(httpService);
        const url = 'primary-connection';
        const response = await httpService.get(url);
        if (response.status == 404) {
            return '';
        }
        handleBadResponse(response);
        const apiConnection = (await response.json());
        return apiConnection.id;
    }, [httpService]);
    const workAreaApi = useMemo(() => ({
        initialized: httpService != undefined,
        addOne,
        getAll,
        updateOne,
        upgradeOne,
        deleteMany,
        addSynced,
        getSynced,
        deleteSynced,
        addDriveSynced,
        getDriveSynced,
        setMultiMode,
        deleteDriveSynced,
        setPrimaryConnection,
        unsetPrimaryConnection,
        getPrimaryConnection
    }), [
        addDriveSynced,
        addOne,
        addSynced,
        deleteDriveSynced,
        deleteMany,
        deleteSynced,
        getAll,
        getDriveSynced,
        getPrimaryConnection,
        getSynced,
        httpService,
        setMultiMode,
        setPrimaryConnection,
        unsetPrimaryConnection,
        updateOne,
        upgradeOne
    ]);
    return workAreaApi;
}
